export interface InsightSections {
  keyFindings: string[]
  recommendations: string[]
  /** True when the text carried explicit section headings (or came from structured arrays). */
  hasSections: boolean
}

const KEY_FINDINGS_HEADING = /^key\s+findings:?$/i
const RECOMMENDATIONS_HEADING = /^recommendations:?$/i

function splitParagraphIntoSentences(text: string) {
  const bullets: string[] = []
  let current = ''

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '.') {
      const previous = current.at(-1)
      const next = text[index + 1]
      if (/\d/.test(previous ?? '') && /\d/.test(next ?? '')) {
        current += char
        continue
      }
      if (current.trim()) bullets.push(`${current.trim()}.`)
      current = ''
      continue
    }
    current += char
  }

  if (current.trim()) bullets.push(current.trim())
  return bullets
}

/**
 * Parses the API's plain-text insight: sections headed "Key findings" / "Recommendations",
 * one item per line, sections separated by a blank line. Legacy single-paragraph prose is
 * split into sentences and treated as key findings.
 */
export function parseInsightSections(insight: string | null | undefined): InsightSections {
  const text = String(insight ?? '').trim()
  const empty: InsightSections = { keyFindings: [], recommendations: [], hasSections: false }
  if (!text) return empty

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s•\-*]+/, '').trim())
    .filter((line) => line.length > 0)

  const onlyLine = lines.length === 1 ? lines[0] : undefined
  if (onlyLine !== undefined && !KEY_FINDINGS_HEADING.test(onlyLine) && !RECOMMENDATIONS_HEADING.test(onlyLine)) {
    return { ...empty, keyFindings: splitParagraphIntoSentences(onlyLine.replace(/\s+/g, ' ')) }
  }

  const sections: InsightSections = { ...empty, keyFindings: [], recommendations: [] }
  let target = sections.keyFindings
  for (const line of lines) {
    if (KEY_FINDINGS_HEADING.test(line)) {
      target = sections.keyFindings
      sections.hasSections = true
    } else if (RECOMMENDATIONS_HEADING.test(line)) {
      target = sections.recommendations
      sections.hasSections = true
    } else {
      target.push(line)
    }
  }

  return sections
}

export function hasInsightContent(sections: InsightSections) {
  return sections.keyFindings.length > 0 || sections.recommendations.length > 0
}
